import './margin/MarginSettings.less';

import type { UpdateTableRowStylingPayloadType } from 'components/editor/grid/reduxStore/tableRowDefaultStyleSlice';
import type {
  TextDefaultStylesState,
  UpdateTextDefaultStylingPayloadType,
} from 'components/editor/grid/reduxStore/textDefaultStylingSlice';
import type React from 'react';
import { createContext, useRef } from 'react';
import {
  flipPageSizeOrientation,
  getOrientationFromPageSize,
  getPageSizeForOrientation,
  gridDefaultPixelDensity,
  type PageOrientation,
  type PageSize,
} from '../../../shared/gridConfig';
import type { Margin } from './models/Margin.model';
import { MarginSide } from './models/MarginSide.enum';
import { selectContentType, type Currency } from '../../../grid/reduxStore/documentSettingsSlice.ts';
import { useAppSelector } from '../../../grid/reduxStore/Store';
import useNewDocumentSettings from './useNewDocumentSettings.ts';
import { useSectionBackgroundPreview } from 'components/editor/Sections/useSectionBackgroundPreview';
import type { SectionStyles } from 'components/editor/Sections/Section.model';
import { useRepositionBlocksOnCanvasResize } from '../../../hooks/useRepositionBlocksOnCanvasResize';
import { computeEffectivePageSize } from 'hooks/computeEffectivePageSize';
import type { MovedBlock } from '../../../UndoRedo/UpdateDocumentSettingsCommand';

export type DocumentSettingsType = {
  margin: Margin;
  backgroundColor: string;
  textDefaultStyles?: TextDefaultStylesState;
  currencySettings?: Currency;
  pageSize?: PageSize;
};

export interface DocumentSettingsContextType {
  documentSettings: DocumentSettingsType;
  effectiveCanvasWidth: number;
  effectiveCanvasHeight: number;
  updateMargins: (value: string | number, type: MarginSide) => void;
  updateBackgroundColor: (color: string) => void;
  updatePageOrientation: (orientation: PageOrientation) => void;
  updatePageSize: (pageSize: PageSize) => void;
  updateTextDefaultStyle: ({ type, style }: UpdateTextDefaultStylingPayloadType) => void;
  updateTableRowStyle: ({ type, style }: UpdateTableRowStylingPayloadType) => void;
  captureDocumentSettings?: () => void;
  commitBackgroundColor?: (color: string) => void;
  sectionStyles: SectionStyles;
  previewSectionBackgroundColor: (color: string) => void;
  captureSectionStyles: () => void;
  commitSectionBackgroundColor: (color: string) => void;
}

export const DocumentSettingsContext = createContext<DocumentSettingsContextType>({} as DocumentSettingsContextType);

interface DocumentSettingsProviderProps {
  children: any;
}

export type SetDocumentSettingsType = (
  newDocumentSettings: DocumentSettingsType,
  previousDocumentSettings?: DocumentSettingsType,
  movedBlocks?: MovedBlock[]
) => void;

export const DocumentSettingsProvider: React.FC<DocumentSettingsProviderProps> = ({ children }: DocumentSettingsProviderProps) => {
  const { documentSettings, previewDocumentSettings, setDocumentSettings, updateTextDefaultStyle, updateTableRowStyle } =
    useNewDocumentSettings();
  const previousSettingsRef = useRef<DocumentSettingsType | null>(null);

  const contentType = useAppSelector(selectContentType);
  const { sectionStyles, previewSectionBackgroundColor, captureSectionStyles, commitSectionBackgroundColor } =
    useSectionBackgroundPreview();

  const effectivePageSize = computeEffectivePageSize(contentType, documentSettings, sectionStyles);
  const effectiveCanvasWidth = effectivePageSize.width;
  const effectiveCanvasHeight = effectivePageSize.height;

  const repositionBlocksOnCanvasResize = useRepositionBlocksOnCanvasResize();

  const resizePage = async (newPageSize: PageSize) => {
    const movedBlocks = await repositionBlocksOnCanvasResize(effectiveCanvasWidth, newPageSize.width).catch(() => []);
    setDocumentSettings({ ...documentSettings, pageSize: newPageSize }, undefined, movedBlocks);
  };

  const updatePageOrientation = async (orientation: PageOrientation) => {
    if (getOrientationFromPageSize(effectivePageSize) === orientation) return;
    await resizePage(flipPageSizeOrientation(effectivePageSize));
  };

  const updatePageSize = async (pageSize: PageSize) => {
    await resizePage(getPageSizeForOrientation(pageSize.label, getOrientationFromPageSize(effectivePageSize)));
  };

  const captureDocumentSettings = () => {
    previousSettingsRef.current = { ...documentSettings };
  };

  const commitBackgroundColor = (color: string) => {
    if (!previousSettingsRef.current) {
      setDocumentSettings({ ...documentSettings, backgroundColor: color });
      return;
    }

    if (previousSettingsRef.current.backgroundColor !== color) {
      setDocumentSettings({ ...documentSettings, backgroundColor: color }, previousSettingsRef.current);
    }

    previousSettingsRef.current = null;
  };

  const updateBackgroundColor = (color: string) => {
    if (previewDocumentSettings) {
      previewDocumentSettings({ backgroundColor: color });
      return;
    }
    setDocumentSettings({ ...documentSettings, backgroundColor: color });
  };

  const updateMargins = (value: string | number, type: MarginSide) => {
    const valueInInches = typeof value === 'string' ? parseFloat(value) : value;
    if (valueInInches < 0) return;

    const valueInPixels = valueInInches * gridDefaultPixelDensity;
    if ((type === MarginSide.LEFT || type === MarginSide.RIGHT) && valueInPixels > effectiveCanvasWidth) {
      return;
    }

    if ((type === MarginSide.TOP || type === MarginSide.BOTTOM) && valueInPixels > effectiveCanvasHeight) {
      return;
    }

    const newMargin: Margin = { ...documentSettings.margin, [MarginSide[type].toLowerCase()]: parseFloat(valueInPixels.toFixed(2)) };
    setDocumentSettings({ ...documentSettings, margin: newMargin });
  };

  return (
    <DocumentSettingsContext.Provider
      value={{
        documentSettings,
        effectiveCanvasWidth,
        effectiveCanvasHeight,
        updateMargins,
        updateBackgroundColor,
        updatePageOrientation,
        updatePageSize,
        updateTextDefaultStyle,
        updateTableRowStyle,
        captureDocumentSettings,
        commitBackgroundColor,
        sectionStyles,
        previewSectionBackgroundColor,
        captureSectionStyles,
        commitSectionBackgroundColor,
      }}
    >
      {children}
    </DocumentSettingsContext.Provider>
  );
};
